import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import { adminCreateUser, fetchServices, fetchUserDetails } from '../services/apiService';
import { AuthContext } from '../context/AuthContext';
import menuItemsByService from '../categories/serviceMenus';
import { useSidebar } from '../context/SidebarContext';

const translateError = (error) => {
  const translations = {
    'Username has already been taken': 'El nombre de usuario ya ha sido tomado',
    'Email has already been taken': 'El correo electrónico ya ha sido tomado',
  };

  return error.map((err) => translations[err] || err);
};

const UserRegistrationForm = () => {
  const initialState = {
    email: '',
    username: '',
    role: 'user',
    password: '',
    passwordConfirmation: '',
    service_ids: [],
    permiso_cre: '',
    telefono: '',
  };

  const [userData, setUserData] = useState(initialState);
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isSidebarOpen } = useSidebar();
  const [userServices, setUserServices] = useState([]);
  const { authTokens, impersonatedUserId } = useContext(AuthContext);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const fetchedServices = await fetchServices(authTokens);
        setServices(fetchedServices);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };

    loadServices();
  }, [authTokens]);

  useEffect(() => {
    const loadUserServices = async () => {
      if (authTokens && (impersonatedUserId || authTokens.userRole !== 'admin')) {
        try {
          const userIdToFetch = impersonatedUserId || authTokens.userId;
          const userDetails = await fetchUserDetails(userIdToFetch, authTokens);
          const serviceOrder = {
            'Anexo 30 y 31 2022': 1,
            'Anexo 30 y 31 2023': 2,
            'Anexo 30 y 31 2024': 3,
            'Anexo 30 y 31 2025': 4,
            'Dictamen de Diseño': 5,
            'NOM-016-CRE-2016': 6,
            'Anexo 30 y 31 para transporte o distribución': 7,
          };
          const sortedServices = userDetails.services.sort(
            (a, b) => (serviceOrder[a.name] || 0) - (serviceOrder[b.name] || 0),
          );
          setUserServices(sortedServices);
        } catch (error) {
          console.error('Error al cargar los servicios del usuario:', error);
        }
      }
    };

    loadUserServices();
  }, [authTokens, impersonatedUserId]);

  const isServiceSelected = (serviceId) => userData.service_ids.includes(serviceId.toString());

  const handleServiceChange = (e) => {
    const { value, checked } = e.target;
    let newServiceIds = [...userData.service_ids];
    if (checked) {
      newServiceIds.push(value);
    } else {
      newServiceIds = newServiceIds.filter((id) => id !== value);
    }
    setUserData({ ...userData, service_ids: newServiceIds });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (userData.password !== userData.passwordConfirmation) {
      setError('La contraseña y la confirmación de la contraseña no coinciden.');
      return;
    }

    try {
      const payload = { ...userData };
      if (payload.role !== 'user') delete payload.permiso_cre;
      if (payload.role !== 'admin') delete payload.telefono;
      const response = await adminCreateUser(payload, authTokens);
      console.log('Usuario registrado:', response);
      setSuccess('Registro exitoso. Bienvenido a la plataforma!');
      setUserData(initialState);
    } catch (error) {
      console.error('Error al registrar el usuario:', error.response ? error.response.data : error);
      const { errors } = error.response.data;
      const translatedErrors = translateError(Object.values(errors).flat());
      setError(translatedErrors.join('. ') || 'No se pudo registrar el usuario. Por favor, verifica tus datos.');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`fixed md:fixed top-16 left-0 h-[calc(100vh-4rem)] overflow-y-auto transform
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          transition duration-200 ease-in-out
          w-64 bg-white shadow-lg z-40`}
      >
        <div className="p-8 border-b">
          <img src="/images/logo2.png" alt="Logo Petrotec" className="h-32 w-auto mx-auto" />
        </div>
        <nav className="mt-6">
          <Link
            to="/dashboard"
            className={`
              block px-4 py-3 hover:bg-blue-50 transition-colors duration-200
              text-gray-700 hover:text-blue-600 border-b border-gray-100
            `}
          >
            Inicio
          </Link>
          <div className="px-4 py-2">
            <h2 className="text-xs uppercase font-semibold text-gray-600 tracking-wide">
              Servicios Disponibles
            </h2>
          </div>
          {userServices.map((service) => {
            const firstMenuItem = menuItemsByService[service.name]?.[0];
            return (
              <Link
                key={service.id}
                to={firstMenuItem?.path || '#'}
                className="block px-4 py-3 hover:bg-blue-50 transition-colors duration-200 text-gray-700 hover:text-blue-600"
              >
                {service.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-200 ${isSidebarOpen ? 'md:ml-64' : ''}`}>

        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Registro de Usuarios</h1>
            <p className="mt-2 text-gray-600">Registra nuevos usuarios en la plataforma.</p>
          </div>

          <div className="bg-white shadow-lg rounded-lg p-8">
            {error && <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">{error}</div>}
            {success && <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-400 text-green-700">{success}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email:</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={userData.email}
                  onChange={handleChange}
                  required
                  className={`
                    mt-1 block w-full border border-gray-300 rounded-md
                    shadow-sm py-2 px-3 focus:outline-none
                    focus:ring-blue-500 focus:border-blue-500
                  `}
                />
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">Nombre de Usuario:</label>
                <input
                  id="username"
                  type="text"
                  name="username"
                  value={userData.username}
                  onChange={handleChange}
                  required
                  className={`
                    mt-1 block w-full border border-gray-300
                    rounded-md shadow-sm py-2 px-3 focus:outline-none
                    focus:ring-blue-500 focus:border-blue-500
                  `}
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">Rol:</label>
                <select
                  id="role"
                  name="role"
                  value={userData.role}
                  onChange={handleChange}
                  required
                  className={`
                    mt-1 block w-full border border-gray-300
                    rounded-md shadow-sm py-2 px-3 focus:outline-none
                    focus:ring-blue-500 focus:border-blue-500
                  `}
                >
                  <option value="">Seleccionar Rol</option>
                  <option value="user">Estación</option>
                  <option value="admin">Inspector</option>
                  <option value="super_admin">Gerente</option>
                </select>
              </div>

              {userData.role === 'user' && (
                <div>
                  <label htmlFor="permiso_cre" className="block text-sm font-medium text-gray-700">
                    Permiso CRE:
                  </label>
                  <input
                    id="permiso_cre"
                    type="text"
                    name="permiso_cre"
                    value={userData.permiso_cre}
                    onChange={handleChange}
                    placeholder="CRE-123"
                    className={`
                      mt-1 block w-full border border-gray-300 rounded-md
                      shadow-sm py-2 px-3 focus:outline-none
                      focus:ring-blue-500 focus:border-blue-500
                    `}
                  />
                </div>
              )}

              {userData.role === 'admin' && (
                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-gray-700">
                    Teléfono:
                  </label>
                  <input
                    id="telefono"
                    type="tel"
                    name="telefono"
                    value={userData.telefono}
                    onChange={handleChange}
                    placeholder="Número de teléfono"
                    className={`
                      mt-1 block w-full border border-gray-300 rounded-md
                      shadow-sm py-2 px-3 focus:outline-none
                      focus:ring-blue-500 focus:border-blue-500
                    `}
                  />
                </div>
              )}

              <div>
                <p className="block text-sm font-medium text-gray-700">Servicios Activos:</p>
                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service) => (
                    <div key={service.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <input
                        id={`service-${service.id}`}
                        type="checkbox"
                        name="service_ids"
                        value={service.id}
                        checked={isServiceSelected(service.id)}
                        onChange={handleServiceChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`service-${service.id}`} className="ml-2 block text-sm text-gray-900">
                        {service.name}
                      </label>
                      {isServiceSelected(service.id) && <FaCheck className="ml-2 text-green-500" />}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Contraseña:</label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={userData.password}
                  onChange={handleChange}
                  required
                  className={`
                    mt-1 block w-full border border-gray-300
                    rounded-md shadow-sm py-2 px-3 focus:outline-none
                    focus:ring-blue-500 focus:border-blue-500
                  `}
                />
              </div>

              <div>
                <label
                  htmlFor="passwordConfirmation"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirmación de Contraseña:
                </label>
                <input
                  id="passwordConfirmation"
                  type="password"
                  name="passwordConfirmation"
                  value={userData.passwordConfirmation}
                  onChange={handleChange}
                  required
                  className={`
                    mt-1 block w-full border border-gray-300
                    rounded-md shadow-sm py-2 px-3 focus:outline-none
                    focus:ring-blue-500 focus:border-blue-500
                  `}
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600
                text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Registrar Usuario
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegistrationForm;
